import {
	Fragment,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { TreeAccordion } from "client/Components";
import { isNil } from "client/Misc/utils";
import { ActiveFilter } from "../common";
import { ExpandCollapseNode } from "./ExpandCollapseNode";
import { HubDataSource } from "client/Api/Hub/hub";
import { FollowCategory } from "./FollowCategory";
import {
	HubSidebarValue,
	KnowledgeItemCategoryNode,
	KnowledgeItemGroupNode,
	KnowledgeItemRootNode
} from "./HubSidebar";
import { KnowledgeItemViewIdentifier } from "../KnowledgeItems/KnowledgeItemsPage";
import { ItemContent } from "./ItemContent";

export type KnowledgeItemSidebarSectionProps = {
	selectedValue: HubSidebarValue | undefined;
	item: KnowledgeItemRootNode;
	collapsedItemsToDisplay: number;
	activeFilter: ActiveFilter | null;
	onSelect: (value: HubSidebarValue) => void;
	areActionsAvailable: boolean;
	isMobile: boolean;
};

export const KnowledgeItemSidebarSection = (props: KnowledgeItemSidebarSectionProps): ReactElement => {
	const {
		selectedValue,
		item,
		collapsedItemsToDisplay,
		activeFilter,
		onSelect,
		areActionsAvailable,
		isMobile
	} = props;

	const {
		children
	} = item;

	const [shouldShowAll, setShouldShowAll] = useState<boolean>(false);

	const [expandedItemIds, setExpandedItemIds] = useState<ReadonlySet<string>>(
		() => new Set<string>([rootExpandId])
	);

	const isItemSelected = useCallback(
		(item: KnowledgeItemCategoryNode | KnowledgeItemGroupNode): boolean => {
			if (isNil(selectedValue)
				|| selectedValue.source !== HubDataSource.KnowledgeItem
			) {
				return false;
			}

			const { viewIdentifier } = selectedValue;

			if (isGroupNode(item)) {
				return viewIdentifier.kind === "groupId"
					&& viewIdentifier.groupId === item.id;
			} else {
				return viewIdentifier.kind === "categoryId"
					&& viewIdentifier.categoryId === item.id;
			}
		},
		[selectedValue]
	);

	useEffect(
		(): void => {
			if (isNil(selectedValue) || selectedValue.source !== item.source) {
				return;
			}

			const pathToSelectedItem: readonly string[] = getParentNodeIds(
				selectedValue.viewIdentifier,
				children
			);

			setExpandedItemIds(
				ids => {
					const newSet: Set<string> = new Set<string>(ids);
					newSet.add(rootExpandId);

					for (const nodeId of pathToSelectedItem) {
						newSet.add(nodeId);
					}

					return newSet;
				}
			);

			setShouldShowAll(
				(shouldShowAll) => {
					return !shouldShowAll
						? children.some(
							(x, index) => (pathToSelectedItem.includes(getItemId(x)) || isItemSelected(x))
								&& index >= collapsedItemsToDisplay
						)
						: shouldShowAll;
				}
			);
		},
		[
			children,
			collapsedItemsToDisplay,
			isItemSelected,
			item.source,
			selectedValue
		]
	);

	const isExpanded = useCallback(
		(itemId: string): boolean => expandedItemIds.has(itemId),
		[expandedItemIds]
	);

	const toggleExpansionHandler = useCallback(
		(itemId: string): void => {
			setExpandedItemIds(ids => {
				const newIds: Set<string> = new Set(ids);

				if (!newIds.delete(itemId)) {
					newIds.add(itemId);
				}

				return newIds;
			});
		},
		[]
	);

	const isTagFilterActive: boolean = activeFilter?.kind === "tag";

	const displayedChildren = useMemo(
		(): readonly (KnowledgeItemCategoryNode | KnowledgeItemGroupNode)[] => children.slice(
			0,
			(shouldShowAll || isTagFilterActive)
				? children.length
				: collapsedItemsToDisplay
		),
		[
			children,
			shouldShowAll,
			isTagFilterActive,
			collapsedItemsToDisplay
		]
	);

	const shouldRenderMoreButton: boolean = !isTagFilterActive
		&& children.length > collapsedItemsToDisplay;

	const isRootExpanded: boolean = isExpanded(rootExpandId);

	return (
		<TreeAccordion.Node>
			<TreeAccordion.Item
				icon={item.icon}
				expansionToggle="visible"
				isExpanded={isRootExpanded}
				onClick={() => toggleExpansionHandler(rootExpandId)}
				onToggleExpansion={() => toggleExpansionHandler(rootExpandId)}
				trackingId={`${tagPrefix}-node`}
				title={item.name}
			>
				<ItemContent
					title={item.name}
					newItemsIndicator={!isRootExpanded
						? item.newItemsCount
						: undefined
					}
					isActive={false}
				/>
			</TreeAccordion.Item>
			<TreeAccordion.SubTree
				isCollapsed={!isRootExpanded}
				trackingId={`${tagPrefix}-sub-tree`}
			>
				{displayedChildren.map((x): ReactElement => {
					const nodeId: string = getItemId(x);

					return (
						<Fragment key={nodeId}>
							<TreeAccordion.Node>
								<Item
									item={x}
									indentionLevel={1}
									isExpanded={isExpanded}
									isItemSelected={isItemSelected}
									toggleExpansion={toggleExpansionHandler}
									onSelect={onSelect}
									areActionsAvailable={areActionsAvailable}
								/>
							</TreeAccordion.Node>

							{x.id === "all" && !isMobile && (
								<TreeAccordion.Node>
									<TreeAccordion.Separator
										indentionLevel={1}
									/>
								</TreeAccordion.Node>
							)}
						</Fragment>
					);
				})}

				{shouldRenderMoreButton && (
					<ExpandCollapseNode
						isExpanded={shouldShowAll}
						onIsExpandedChange={setShouldShowAll}
						trackingId={`${tagPrefix}-expand`}
					/>
				)}
			</TreeAccordion.SubTree>
		</TreeAccordion.Node>
	);
};
KnowledgeItemSidebarSection.displayName = "KnowledgeItemSidebarSection";

type ItemProps = {
	item: KnowledgeItemCategoryNode | KnowledgeItemGroupNode;
	indentionLevel: number;
	isExpanded: (id: string) => boolean;
	isItemSelected: (item: KnowledgeItemCategoryNode | KnowledgeItemGroupNode) => boolean;
	toggleExpansion: (id: string) => void;
	onSelect: (value: HubSidebarValue) => void;
	areActionsAvailable: boolean;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		indentionLevel,
		isExpanded,
		isItemSelected,
		toggleExpansion,
		onSelect,
		areActionsAvailable
	} = props;

	const clickHandler = useCallback(
		(): void => {
			let value: HubSidebarValue;

			if (isGroupNode(item)) {
				value = {
					source: HubDataSource.KnowledgeItem,
					viewIdentifier: {
						kind: "groupId",
						groupId: item.id
					}
				};
			} else {
				value = {
					source: HubDataSource.KnowledgeItem,
					viewIdentifier: {
						kind: "categoryId",
						categoryId: item.id
					}
				};
			}

			onSelect(value);
		},
		[item, onSelect]
	);

	const isSelected: boolean = isItemSelected(item);

	if (isGroupNode(item)) {
		const itemId: string = getItemId(item);

		return (
			<>
				<TreeAccordion.Item
					expansionToggle="visible"
					isExpanded={isExpanded(itemId)}
					onClick={clickHandler}
					isSelected={isSelected}
					onToggleExpansion={() => toggleExpansion(itemId)}
					indentionLevel={indentionLevel}
					trackingId={`${tagPrefix}-${itemId}`}
					title={item.name}
				>
					<ItemContent
						title={item.name}
						newItemsIndicator={item.hasNewItems}
						isActive={isSelected}
					/>
				</TreeAccordion.Item>
				<TreeAccordion.SubTree
					isCollapsed={!isExpanded(itemId)}
					trackingId={`${tagPrefix}-sub-tree|${itemId}`}
				>
					{item
						.children
						.map(
							x => (
								<TreeAccordion.Node key={getItemId(x)}>
									<Item
										item={x}
										indentionLevel={indentionLevel + 1}
										isExpanded={isExpanded}
										isItemSelected={isItemSelected}
										toggleExpansion={toggleExpansion}
										onSelect={onSelect}
										areActionsAvailable={areActionsAvailable}
									/>
								</TreeAccordion.Node>
							)
						)
					}
				</TreeAccordion.SubTree>
			</>
		);
	}

	return (
		<TreeAccordion.Item
			isSelected={isSelected}
			onClick={clickHandler}
			indentionLevel={indentionLevel}
			trackingId={`${tagPrefix}-${getItemId(item)}`}
			action={
				areActionsAvailable
					&& typeof item.id === "number"
						? (
							<FollowCategory
								categoryId={item.id}
								categoryName={item.name}
								isFollowing={item.isFollowing}
							/>
						)
						: undefined
			}
			title={item.name}
		>
			<ItemContent
				title={item.name}
				newItemsIndicator={item.newItemsCount}
				isActive={isSelected}
			/>
		</TreeAccordion.Item>
	);

};
Item.displayName = "Item";

const tagPrefix: string = "knowledge-item";
const rootExpandId: string = "knowledge-item";

function getItemId(
	node: KnowledgeItemCategoryNode | KnowledgeItemGroupNode
): string {
	if (isGroupNode(node)) {
		return `group-${node.id}`;
	}

	if (isNil(node.id)) {
		return "overview";
	}

	return `category-${node.id}`;
}

function getParentNodeIds(
	viewIdentifier: KnowledgeItemViewIdentifier,
	children: readonly (KnowledgeItemCategoryNode | KnowledgeItemGroupNode)[]
): readonly string[] {
	if (viewIdentifier.kind === "categoryId"
		&& typeof viewIdentifier.categoryId !== "number"
	) {
		return [];
	}

	for (const child of children) {
		const result: (readonly string[]) | null = getParentNodeIdsRecursive(viewIdentifier, child);

		if (result) {
			return result;
		}
	}

	return [];
}

function getParentNodeIdsRecursive(
	viewIdentifier: KnowledgeItemViewIdentifier,
	node: KnowledgeItemCategoryNode | KnowledgeItemGroupNode
): (readonly string[]) | null {
	if (isGroupNode(node)) {
		if (viewIdentifier.kind === "groupId"
			&& viewIdentifier.groupId === node.id
		) {
			return [];
		}

		for (const child of node.children) {
			const path: (readonly string[]) | null = getParentNodeIdsRecursive(viewIdentifier, child);

			if (path) {
				return [getItemId(node), ...path];
			}
		}
	} else {
		if (viewIdentifier.kind === "categoryId"
			&& viewIdentifier.categoryId === node.id
		) {
			return [];
		}
	}

	return null;
}

function isGroupNode(item: KnowledgeItemCategoryNode | KnowledgeItemGroupNode): item is KnowledgeItemGroupNode {
	return typeof (item as KnowledgeItemGroupNode).children !== "undefined";
}
